import { CloseOutlined, Comment } from '@mui/icons-material';
import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import {
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Tooltip, Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useStoreState } from 'pullstate';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CommentsTimelineGoalForm from '../../components/CommentsTimelineGoalForm.jsx';
import DataGridTooltips from '../../components/DataGridTooltips.jsx';
import { AppStore } from '../../stores/AppStore.js';
import {
    checkHasAnyComments,
    handleCommentsChange as handleCommentsChangeUtil,
    handleStatusChange as handleStatusChangeUtil,
    loadAllGoalComments,
    transformCheckinGoalData,
    transformGoalData
} from '../../utils/checkInFunctions.js';
import { getGoals } from '../../utils/getData.js';
import GoalForm from '../goals/GoalForm.jsx';
import getDataGridStyles from '../../components/styles/dataGridStyles.js';

const CheckinGoals = ({ setCheckinGoalData }) => {
    const { playerId, checkinId, peopleBuilderId } = useParams()
    const checkIns = useStoreState(AppStore, s => s.checkIns)
    const checkInGoals = useStoreState(AppStore, s => s.checkInGoals)
    const [goals, setGoals] = useState([])
    const [openDialogGoalComment, setOpenDialogGoalComment] = useState(false)
    const [goalSelectedToComment, setGoalSelectedToComment] = useState([])
    const [comment, setComment] = useState('')
    const [comments, setComments] = useState([])
    const [checkin, setCheckin] = useState({})
    const [checkInGoalComments, setCheckInGoalComments] = useState([])
    const [goalComments, setGoalComments] = useState([])
    const [openGoalModal, setOpenGoalModal] = useState(false)
    const [loadingGoals, setLoadingGoals] = useState(true)
    const [pagination, setPagination] = useState({ page: 0, pageSize: 6 })

    const handleCloseDialog = () => {
        setOpenDialogGoalComment(false)
    }

    const getCheckin = () => {
        const checkinData = checkIns?.find((checkin) => checkin.id === parseInt(checkinId))
        setCheckin(checkinData)
    }

    const handleOpenDialog = (goal) => {
        setGoalSelectedToComment(goal)
        // Load all comments for the specific goal from all non-archived checkins

        loadAllGoalComments(
            goal.goal_id,
            goals,
            checkIns,
            checkInGoals,
            setComments,
            setCheckInGoalComments,
            setGoalComments
        )
        setOpenDialogGoalComment(true)
    }

    const handleClose = () => {
        setOpenDialogGoalComment(false)
    }

    const handleStatusChange = (id, value) => {
        handleStatusChangeUtil(id, value, goals, setGoals, setCheckinGoalData, () => { })
    }

    const handleCommentsChange = (id, value) => {
        handleCommentsChangeUtil(
            id,
            value,
            goals,
            setGoals,
            setCheckinGoalData,
            () => { }, // Empty function since setEditingId is not used in this component
            setComments,
            setCheckInGoalComments
        )
    }

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            hide: true
        },
        {
            field: 'goalTitle',
            headerClassName: 'header-class',
            headerName: 'Goal Title',
            width: 150,
            flex: 2,
            renderCell: (params) => {
                return (
                    <DataGridTooltips value={params.value || ''} width={params.colDef.computedWidth}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {params.value}
                        </div>
                    </DataGridTooltips>
                )
            }
        },
        // { field: 'targetDate', headerClassName: 'header-class', hide: true, headerName: 'Target Date', width: 150, flex: 1 },
        { field: 'lastCheckinDate', headerClassName: 'header-class', headerName: 'Last Check-in Date', width: 150, flex: 1 },
        {
            field: 'check_in_goal_status',
            headerName: 'Check-in Status',
            headerClassName: 'header-class',
            editable: (params) => !params.row.goal_status.includes('archived'),
            width: 150,
            renderCell: (params) => {
                const isDisabled = params.row.goal_status === 'archived'

                return (
                    <Select
                        value={params?.value ?? ''}
                        disabled={isDisabled}
                        onChange={(e) => handleStatusChange(params.id, e.target.value)}
                        fullWidth
                        sx={{ height: '80%' }}
                        autoFocus
                    >
                        <MenuItem value="Complete">Complete</MenuItem>
                        <MenuItem value="Delayed">Delayed</MenuItem>
                        <MenuItem value="Not Started">Not Started</MenuItem>
                        <MenuItem value="On Track">On Track</MenuItem>

                    </Select >
                )
            }
        },
        {
            field: 'hasComments',
            headerName: 'Comments',
            headerClassName: 'header-class',
            renderCell: (params) => {
                const isDisabled = params.row.goal_status === 'archived'

                // Check if there are comments in any checkin for this goal
                const hasAnyComments = checkHasAnyComments(params.row.goal_id, goals, checkIns, checkInGoals)

                return !isDisabled ? (
                    hasAnyComments ? (
                        <div>
                            <Tooltip title='Has Comments'>
                                <IconButton
                                    onClick={() => {
                                        handleOpenDialog(params.row)
                                    }}>
                                    <MarkUnreadChatAltIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        <div>
                            <Tooltip title='No Comments'>
                                <IconButton
                                    onClick={() => {
                                        handleOpenDialog(params.row)
                                    }}>
                                    <CommentsDisabledIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )
                ) : (
                    <div>
                        <IconButton disabled>
                            <Comment />
                        </IconButton>
                    </div >
                )
            }
        },

    ]

    const fetchGoals = async () => {
        const data = await getGoals()
        const goalsFiltered = data.filter((goal) => goal.people_builder_id === parseInt(peopleBuilderId))
        let dataFiltered = goalsFiltered.filter((goal) => goal.people_builder.player_id === playerId && goal.goal_status !== 'archived')
        let checkinGoalsIds = checkin?.check_in_goals?.map((goal) => goal.goal_id)

        if (dataFiltered.length === 0) {
            dataFiltered = data.filter((goal) => checkinGoalsIds?.includes(goal.id))
        }

        const goalData = transformGoalData(dataFiltered)
        goalData.sort((a, b) => {
            return a.goalTitle.localeCompare(b.goalTitle)
        })
        setGoals(goalData)
        setCheckinGoalData(goalData)
        setLoadingGoals(false)

    }

    const getCheckinGoals = useCallback(async () => {
        const goalData = transformCheckinGoalData(checkin?.check_in_goals, peopleBuilderId)
        goalData.sort((a, b) => {
            return a.goalTitle.localeCompare(b.goalTitle)
        })

        setGoals(goalData)
        setCheckinGoalData(goalData)
        setLoadingGoals(false)
    }, [checkin, peopleBuilderId])

    const getRowClassName = useCallback((params) => {

        if (params.row.goal_status === 'archived') {
            return 'disabled-row';
        } else {
            return '';
        }
    }, [goals, checkin]);

    useEffect(() => {
        if (!checkinId) {
            fetchGoals()
        } else {
            getCheckinGoals()
        }
    }, [checkin])

    useEffect(() => {
        getCheckin()
    }, [checkIns])

    return (
        <>
            <DataGrid
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false
                        }
                    }
                }}
                sx={getDataGridStyles}
                rows={goals}
                columns={columns}
                pageSize={10}
                paginationModel={pagination}
                pageSizeOptions={[6]}
                rowsPerPageOptions={[10]}
                getRowClassName={getRowClassName}
                loading={loadingGoals}
                localeText={{
                    noRowsLabel: 'No goals found',
                    footerRowSelected: (count) => `${count} goal(s) selected`,
                }}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton'
                    }
                }}
                disableRowSelectionOnClick
                disableColumnResize
                disableColumnMenu
                disableColumnSorting
                isCellEditable={(params) => !params.row.goal_status.includes('archived')}
                onPaginationModelChange={(params) => setPagination((prev) => ({ ...prev, pageSize: 6, page: params.page }))}
                onCellClick={(params) => {
                    setGoalSelectedToComment(params.row)
                    if (params.field === 'check_in_goal_status') {
                        return
                    } else if (params.field !== 'hasComments') {
                        setOpenGoalModal(true)
                        // navigate(`/goal-form/people-builder/${params.row.people_builder_id}/goal/${params.row.goal_id}`)
                        // window.open(`/goal-form/people-builder/${params.row.people_builder_id}/goal/${params.row.goal_id}`, '_blank')
                    }
                }}

            />
            <Dialog
                open={openDialogGoalComment}
                onClose={handleClose}
                fullWidth
                maxWidth='md'
            >
                <DialogActions>
                    <IconButton onClick={handleCloseDialog} size='small'>
                        <CloseOutlined />
                    </IconButton>
                </DialogActions>
                <DialogContent>
                    <Stack alignItems='start' ml={2}>
                        <Typography variant='p' sx={{ textAlign: 'center' }}>Goal Name: {goalSelectedToComment?.goalTitle}</Typography>
                        <Typography variant='p' sx={{ textAlign: 'center' }}>Goal Term: {goalSelectedToComment?.goalType}</Typography>
                        <Typography variant='p' sx={{ textAlign: 'center' }}>Goal Status: {goalSelectedToComment?.goal_status}</Typography>
                    </Stack>
                    <Divider sx={{ marginTop: 2, ml: 2 }} />
                    <CommentsTimelineGoalForm
                        setComment={setComment}
                        comment={comment}
                        setComments={setComments}
                        comments={comments}
                        setCheckInGoalComments={setCheckInGoalComments}
                        checkInGoalComments={checkInGoalComments}
                        goalComments={goalComments}
                        setGoalComments={setGoalComments}
                        goalSelectedToComment={goalSelectedToComment}
                        handleCommentsChange={handleCommentsChange}
                        goalId={goalSelectedToComment.goal_id}
                        showCheckinComments={true}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={openGoalModal} onClose={() => setOpenGoalModal(false)} maxWidth="xl" fullWidth>
                <DialogActions>
                    <IconButton onClick={() => setOpenGoalModal(false)} size='small'>
                        <CloseOutlined />
                    </IconButton>
                </DialogActions>
                <DialogContent style={{ overflow: "hidden" }}>
                    <GoalForm goalId={goalSelectedToComment.goal_id} fromModal={true} />
                </DialogContent>
            </Dialog>
        </>

    )
}

export default CheckinGoals
